/**
 * ============================================================================
 * CREATE NOTE MODAL v3.7 - AUTO-TITEL & PERSON FIX
 * ============================================================================
 *
 * ✅ v3.7: AUTO-TITEL & PERSON FIX
 *          - Auto-Titel aus Content generieren wenn leer
 *          - person: null statt "alle" für Backend
 *
 * ✅ v3.6: DEBUG PERSON FIELD
 * ✅ v3.5: HAUSHALTSMITGLIEDER ZUWEISUNG
 *
 * @version 3.7.0 - Auto-Titel & Person Fix
 */

import React, { useState, useEffect, useCallback } from 'react';

console.log('📝 CreateNoteModal v3.7 loaded - AUTO-TITEL & PERSON FIX 🔧');

// ============================================================================
// CONSTANTS
// ============================================================================
const NOTE_CATEGORIES = [
  { id: 'personal', name: '🏠 Persönlich' },
  { id: 'work', name: '💼 Arbeit' },
  { id: 'ideas', name: '💡 Ideen' },
  { id: 'important', name: '⭐ Wichtig' }
];

const NOTE_COLORS = [
  { id: 'yellow', name: '🟡 Gelb', hex: '#FEF3C7' },
  { id: 'green', name: '🟢 Grün', hex: '#D1FAE5' },
  { id: 'blue', name: '🔵 Blau', hex: '#DBEAFE' },
  { id: 'pink', name: '🩷 Pink', hex: '#FCE7F3' },
  { id: 'purple', name: '🟣 Lila', hex: '#EDE9FE' },
  { id: 'orange', name: '🟠 Orange', hex: '#FFEDD5' }
];

const DEFAULT_FORM_DATA = {
  title: '',
  content: '',
  category: 'personal',
  color: 'yellow',
  person: 'alle'
};

const DEFAULT_MEMBERS = [
  { id: 'alle', value: 'alle', name: 'Alle', avatar: '👥' }
];

// ============================================================================
// SAFE TRANSLATION HELPER
// ============================================================================
const safeT = (t, key, fallback) => {
  if (!t) return fallback;
  const result = t(key);
  if (result && typeof result === 'object') {
    return fallback;
  }
  return result ?? fallback;
};

// ============================================================================
// STYLES - PLANIVIO COMPACT DESIGN
// ============================================================================
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },
  modal: {
    background: '#FFFFFF',
    borderRadius: '16px',
    border: '2px solid #B8D4DC',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(74, 144, 164, 0.15)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid #B8D4DC',
    background: '#f3f4f6'
  },
  headerTitle: {
    margin: 0,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  closeBtn: {
    width: '24px',
    height: '24px',
    border: 'none',
    background: 'transparent',
    color: '#9ca3af',
    borderRadius: '6px',
    fontSize: '1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    padding: 0,
    lineHeight: 1
  },
  form: {
    padding: '12px'
  },
  formGroup: {
    marginBottom: '10px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem'
  },
  input: {
    width: '100%',
    padding: '6px 10px',
    border: '1.5px solid #B8D4DC',
    borderRadius: '6px',
    fontSize: '0.85rem',
    background: 'white',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    padding: '6px 10px',
    border: '1.5px solid #B8D4DC',
    borderRadius: '6px',
    fontSize: '0.85rem',
    background: 'white',
    resize: 'vertical',
    fontFamily: 'inherit',
    minHeight: '100px',
    boxSizing: 'border-box',
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '6px 10px',
    border: '1.5px solid #B8D4DC',
    borderRadius: '6px',
    fontSize: '0.8rem',
    background: 'white',
    cursor: 'pointer',
    boxSizing: 'border-box',
    outline: 'none'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '8px',
    borderTop: '1px solid #D4E8EC'
  },
  btnCancel: {
    padding: '6px 12px',
    background: 'white',
    color: '#6B7280',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  btnSave: {
    padding: '6px 12px',
    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)',
    transition: 'all 0.2s'
  },
  btnDelete: {
    padding: '6px 12px',
    background: '#FEF2F2',
    color: '#EF4444',
    border: '1px solid #FECACA',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.2s'
  },
  spacer: {
    flex: 1
  },
  colorPreview: {
    display: 'flex',
    gap: '6px',
    marginTop: '6px'
  },
  colorDot: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'all 0.2s'
  },
  colorDotSelected: {
    border: '2px solid #4A90A4',
    transform: 'scale(1.1)'
  }
};

// ============================================================================
// COMPONENT
// ============================================================================
const CreateNoteModal = ({
  show,
  onClose,
  editingNote,
  onSave,
  onDelete,
  t,
  onTrackEvent,
  memberOptions = []
}) => {

  console.log('📝 CreateNoteModal render, show:', show, 'editingNote:', editingNote?.id);

  const FAMILY_MEMBERS = memberOptions.length > 0 ? memberOptions : DEFAULT_MEMBERS;
  
  // ============================================
  // STATE
  // ============================================
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  // ============================================
  // ANALYTICS HELPER
  // ============================================
  const trackEvent = useCallback((eventName, metadata = {}) => {
    if (onTrackEvent) {
      onTrackEvent(eventName, {
        component: 'CreateNoteModal',
        ...metadata
      });
    }
  }, [onTrackEvent]);

  // ============================================
  // useEffect: Form initialisieren beim Öffnen
  // ============================================
  useEffect(() => {
    if (!show) return;
    
    console.log('📝 Modal opened, initializing form');
    
    trackEvent('note_modal_open', {
      mode: editingNote ? 'edit' : 'create',
      note_id: editingNote?.id
    });
    
    if (editingNote) {
      const rawPerson = editingNote.person || editingNote.assignedTo || editingNote.assigned_to;
      const personValue = (!rawPerson || rawPerson === 'alle') ? 'alle' : String(rawPerson);

      setFormData({
        title: editingNote.title || '',
        content: editingNote.content || '',
        category: editingNote.category || 'personal',
        color: editingNote.color || 'yellow',
        person: personValue
      });
    } else {
      setFormData({
        title: '',
        content: '',
        category: 'personal',
        color: 'yellow',
        person: 'alle'
      });
    }
  }, [show, editingNote, trackEvent]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'category') {
      trackEvent('note_category_change', {
        from: formData.category,
        to: value
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleColorSelect = (colorId) => {
    trackEvent('note_color_change', {
      from: formData.color,
      to: colorId
    });
    
    setFormData(prev => ({
      ...prev,
      color: colorId
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.content.trim()) {
      alert(safeT(t, 'notes.contentRequired', 'Bitte gib eine Notiz ein'));
      return;
    }

    // ✅ v3.7: Auto-Titel aus Content generieren wenn leer
    const autoTitle = formData.title.trim() 
      ? formData.title 
      : formData.content.trim().substring(0, 50) + (formData.content.length > 50 ? '...' : '');

    // ✅ v3.7: Person-Feld für Backend aufbereiten (null statt "alle")
    const personValue = formData.person === 'alle' ? null : formData.person;

    const submitData = {
      ...formData,
      title: autoTitle,
      person: personValue
    };

    trackEvent(editingNote ? 'note_edit' : 'note_create', {
      category: formData.category,
      color: formData.color,
      content_length: formData.content.length
    });

    console.log('📝 Submitting note:', JSON.stringify(submitData));
    onSave(submitData);
  };

  const handleDelete = () => {
    if (editingNote && window.confirm(safeT(t, 'notes.confirmDelete', 'Notiz wirklich löschen?'))) {
      trackEvent('note_delete', {
        note_id: editingNote.id,
        category: editingNote.category,
        color: editingNote.color
      });
      
      onDelete(editingNote.id);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // ============================================
  // EARLY RETURN
  // ============================================
  if (!show) return null;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>
            {editingNote ? '✏️ Notiz bearbeiten' : '➕ Neue Notiz'}
          </h2>
          <button 
            style={styles.closeBtn}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>

          {/* Content - Großes Textarea */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Notiz *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Notiz eingeben..."
              required
              autoFocus
              style={{...styles.textarea, minHeight: '300px', padding: '12px', fontSize: '0.95rem'}}
            />
          </div>

          {/* Category & Color */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Kategorie
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={styles.select}
              >
                {NOTE_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Farbe
              </label>
              <select
                name="color"
                value={formData.color}
                onChange={handleChange}
                style={styles.select}
              >
                {NOTE_COLORS.map(color => (
                  <option key={color.id} value={color.id}>
                    {color.name}
                  </option>
                ))}
              </select>
              
              {/* Color Preview Dots */}
              <div style={styles.colorPreview}>
                {NOTE_COLORS.map(color => (
                  <div
                    key={color.id}
                    onClick={() => handleColorSelect(color.id)}
                    style={{
                      ...styles.colorDot,
                      background: color.hex,
                      ...(formData.color === color.id ? styles.colorDotSelected : {})
                    }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Zuständig (Haushaltsmitglied) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Zuständig
            </label>
            <select
              name="person"
              value={formData.person}
              onChange={handleChange}
              style={styles.select}
            >
              {FAMILY_MEMBERS.map(member => (
                <option key={member.id} value={member.id}>
                  {member.avatar || '👤'} {member.name}
                </option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div style={styles.actions}>
            {editingNote && (
              <button
                type="button"
                style={styles.btnDelete}
                onClick={handleDelete}
              >
                🗑️ Löschen
              </button>
            )}

            <div style={styles.spacer}></div>

            <button
              type="button"
              style={styles.btnCancel}
              onClick={onClose}
            >
              Abbrechen
            </button>

            <button
              type="submit"
              style={styles.btnSave}
            >
              💾 Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateNoteModal;